/*
 * FRUITY — the behaviour layer.
 *
 * The markup in sections/fruity-*.liquid is the static build's DOM, which means
 * it is the state React renders on FIRST PAINT and nothing more. Everything that
 * used to be React state lives here: the slider, the bag configurator, the
 * review list, the drawers, the sheets, the draw-on timeline.
 *
 * Two rules run through the whole file, both learned the hard way on this clone:
 *
 *  1. Read state from the DOM where the DOM is the state. The gallery's active
 *     slide is derived from the track's scrollLeft, never stored — a swipe fires
 *     no click, and a stored index would leave the thumbnails pointing at the
 *     wrong frame.
 *  2. Never display:none something that animates. Closed drawers and sheets are
 *     translated off-canvas and made invisible, exactly as the React build does,
 *     so the transitions have something to transition.
 */
(function () {
  'use strict';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var on = function (el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); };
  var money = function (n) { return '$' + n.toFixed(2); };

  function swapClasses(el, remove, add) {
    if (!el) return;
    remove.split(' ').filter(Boolean).forEach(function (c) { el.classList.remove(c); });
    add.split(' ').filter(Boolean).forEach(function (c) { el.classList.add(c); });
  }

  /* rAF-throttled scroll listener. Every scroll-driven widget on the page uses
   * one of these rather than its own; a page with five naive scroll handlers is
   * a page that drops frames on a phone. */
  function onScrollFrame(fn) {
    var frame = 0;
    function run() { frame = 0; fn(); }
    function handler() { if (!frame) frame = window.requestAnimationFrame(run); }
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    fn();
    return handler;
  }

  function lockScroll(locked) {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /* Whether the cart drawer is currently open. Scroll-driven widgets (the
   * sticky checkout bar, the sticky CTA pill) consult this before showing
   * themselves — a `resize` event (mobile browser chrome collapsing/
   * expanding, which fires freely even while scroll is locked) must never
   * re-reveal them over an open drawer. */
  var fruityCartOpen = false;

  /* ==================================================== announcement bar */
  function initAnnouncement() {
    var bar = $('[data-fruity-announcement]');
    if (!bar) return;
    var text = $('[data-fruity-announcement-text]', bar);
    var payload = $('[data-fruity-announcement-messages]', bar);
    if (!text || !payload) return;
    var messages;
    try { messages = JSON.parse(payload.textContent); } catch (e) { return; }
    if (!messages || messages.length < 2) return;

    var i = 0;
    var dwell = parseInt(bar.getAttribute('data-rotate-ms'), 10) || 4200;
    setInterval(function () {
      swapClasses(text, 'translate-y-0 opacity-100', '-translate-y-1.5 opacity-0');
      setTimeout(function () {
        i = (i + 1) % messages.length;
        text.textContent = messages[i];
        swapClasses(text, '-translate-y-1.5 opacity-0', 'translate-y-0 opacity-100');
      }, 200);
    }, dwell);
  }

  /* ========================================================= header + nav */
  var SCROLL_DELTA = 6;

  function initHeader() {
    var header = $('[data-fruity-header]');
    if (!header) return;
    var lastY = window.scrollY || 0;
    onScrollFrame(function () {
      var y = window.scrollY || 0;
      if (y <= 0) { swapClasses(header, '-translate-y-full', 'translate-y-0'); lastY = 0; return; }
      var diff = y - lastY;
      if (Math.abs(diff) < SCROLL_DELTA) return;
      if (diff > 0) swapClasses(header, 'translate-y-0', '-translate-y-full');
      else swapClasses(header, '-translate-y-full', 'translate-y-0');
      lastY = y;
    });
  }

  function initNavDrawer() {
    var drawer = $('#peachy-nav-drawer');
    var opener = $('[data-fruity-nav-open]');
    if (!drawer || !opener) return;
    var backdrop = drawer.firstElementChild;
    var panel = $('[data-fruity-nav-panel]', drawer);
    var focusables = $$('a, button', drawer);

    function setOpen(open) {
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      drawer.classList.toggle('pointer-events-none', !open);
      swapClasses(backdrop, open ? 'opacity-0' : 'opacity-100', open ? 'opacity-100' : 'opacity-0');
      swapClasses(panel, open ? 'translate-x-full' : 'translate-x-0', open ? 'translate-x-0' : 'translate-x-full');
      focusables.forEach(function (el) { el.tabIndex = open ? 0 : -1; });
      opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      lockScroll(open);
      if (!open) opener.focus();
    }

    on(opener, 'click', function () { setOpen(true); });
    $$('[data-fruity-nav-close]', drawer).forEach(function (b) { on(b, 'click', function () { setOpen(false); }); });
    on(window, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') setOpen(false);
    });
  }

  /* ================================================================= cart */
  // Real Shopify cart, driven off the AJAX Cart API (/cart.js, /cart/add.js,
  // /cart/change.js, /cart/clear.js). `.lines` holds the server's `items`
  // array verbatim (key, id = variant id, quantity, price/line_price in
  // CENTS, image, product_title, variant_title, properties, ...) — this is
  // the same shape dev/fixtures.mjs's lineItem() produces, and the same
  // shape the cart PAGE section reads server-side via `cart.items`.
  //
  // `.save()` is kept as a no-op so the interface below stays identical to
  // what every render function in this file was already written against —
  // the server is now the only source of truth, there is nothing to persist
  // locally.
  var FREE_SHIP_AT = 50;

  function cartRequest(url, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ 'Accept': 'application/json' }, opts.headers || {});
    return fetch(url, opts).then(function (r) {
      return r.text().then(function (text) {
        var data;
        try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
        if (!r.ok) { var err = new Error((data && data.description) || 'Cart request failed'); err.data = data; throw err; }
        return data;
      });
    });
  }

  var cart = {
    lines: [],
    item_count: 0,
    total_price: 0,
    currency: 'USD',
    count: function () { return this.item_count; },
    subtotal: function () { return this.total_price / 100; },
    _hydrate: function (data) {
      this.lines = data.items || [];
      this.item_count = data.item_count || 0;
      this.total_price = data.total_price || 0;
      this.currency = data.currency || this.currency;
      return data;
    },
    load: function () {
      var self = this;
      return cartRequest('/cart.js').then(function (data) { return self._hydrate(data); });
    },
    save: function () { /* server is the source of truth — nothing to persist */ },
    add: function (items) {
      var self = this;
      return cartRequest('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      }).then(function () { return self.load(); });
    },
    setQty: function (key, qty) {
      var self = this;
      return cartRequest('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      }).then(function (data) { return self._hydrate(data); });
    },
    remove: function (key) { return this.setQty(key, 0); },
    clear: function () {
      var self = this;
      return cartRequest('/cart/clear.js', { method: 'POST' }).then(function (data) { return self._hydrate(data); });
    },
  };

  function initCart() {
    var shell = $('[data-fruity-cart]');
    if (!shell) return;
    var panel = $('[data-fruity-cart-panel]', shell);
    var body = $('[data-fruity-cart-body]', shell);
    var title = $('[data-fruity-cart-title]', shell);
    var countSr = $('[data-fruity-cart-count-sr]');
    var cartBtn = $('[data-fruity-cart-open]');

    cart.load().then(render).catch(function () { /* dev renderer has no /cart.js — stay on the default empty state */ });

    function renderCount() {
      var n = cart.count();
      if (countSr) countSr.textContent = n + ' item' + (n === 1 ? '' : 's');
      if (!cartBtn) return;
      cartBtn.setAttribute('aria-label', n > 0 ? 'Cart, ' + n + ' item' + (n === 1 ? '' : 's') : 'Cart');
      var badge = $('[data-fruity-cart-badge]', cartBtn);
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.setAttribute('data-fruity-cart-badge', '');
          badge.className = 'absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-accent px-1 text-[10px] font-extrabold leading-none text-white';
          cartBtn.insertBefore(badge, countSr);
        }
        badge.textContent = String(n);
      } else if (badge) {
        badge.remove();
      }
    }

    var HARD = 'fruity-hard-sm mt-2 rounded-full border-2 border-ink bg-accent px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0';

    function renderBody() {
      var n = cart.count();
      if (title) title.innerHTML = 'Your bag ' + (n > 0 ? '<span class="text-accent">(' + n + ')</span>' : '');

      if (!cart.lines.length) {
        body.className = 'flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center';
        body.innerHTML =
          '<p class="font-display text-[20px] font-extrabold text-heading">Your bag is empty</p>' +
          '<p class="text-sm text-body">Pick your flavours and they will show up here.</p>' +
          '<button type="button" data-fruity-cart-close class="' + HARD + '">Browse flavours</button>';
        on($('[data-fruity-cart-close]', body), 'click', function () { setOpen(false); });
        return;
      }

      var subtotal = cart.subtotal();
      var remaining = Math.max(0, FREE_SHIP_AT - subtotal);
      var pct = Math.min(100, (subtotal / FREE_SHIP_AT) * 100);
      var STEP = 'flex h-7 w-7 items-center justify-center rounded-full border-2 border-ink bg-white text-sm font-bold text-heading transition-transform duration-150 active:scale-95';

      body.className = 'flex flex-1 flex-col overflow-hidden';
      body.innerHTML =
        '<div class="border-b border-ink/10 px-5 py-3">' +
          '<p class="text-xs font-semibold text-heading">' +
            (remaining > 0
              ? 'You are <span class="text-accent">' + money(remaining) + '</span> from free shipping'
              : '<span class="text-success">Free shipping unlocked</span>') +
          '</p>' +
          '<span class="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-ink/10">' +
            '<span class="block h-full rounded-full bg-accent transition-[width] duration-300 ease-out" style="width:' + pct + '%"></span>' +
          '</span>' +
        '</div>' +
        '<ul class="flex-1 overflow-y-auto px-5 py-4">' +
          cart.lines.map(function (l) {
            var flavour = l.variant_title || (l.properties && l.properties.Flavour) || '';
            var name = l.product_title || l.title;
            var img = l.image || (l.featured_image && l.featured_image.url) || '';
            return '<li class="flex gap-3 border-b border-ink/10 py-4 last:border-b-0">' +
              '<div class="relative h-[76px] w-[76px] shrink-0 overflow-hidden rounded-xl border-2 border-ink bg-white">' +
                '<img src="' + img + '" alt="" class="object-cover" style="position:absolute;height:100%;width:100%;left:0;top:0;right:0;bottom:0">' +
              '</div>' +
              '<div class="flex min-w-0 flex-1 flex-col">' +
                '<p class="text-sm font-bold text-heading">' + name + '</p>' +
                '<p class="text-xs text-muted">' + flavour + '</p>' +
                '<div class="mt-auto flex items-center justify-between pt-2">' +
                  '<div class="flex items-center gap-2">' +
                    '<button type="button" aria-label="Decrease ' + flavour + '" data-line-minus="' + l.key + '" class="' + STEP + '">−</button>' +
                    '<span class="w-5 text-center text-sm font-bold text-heading">' + l.quantity + '</span>' +
                    '<button type="button" aria-label="Increase ' + flavour + '" data-line-plus="' + l.key + '" class="' + STEP + '">+</button>' +
                  '</div>' +
                  '<span class="text-sm font-extrabold text-heading">' + money(l.final_line_price != null ? l.final_line_price / 100 : l.line_price / 100) + '</span>' +
                '</div>' +
              '</div>' +
              '<button type="button" aria-label="Remove ' + flavour + '" data-line-remove="' + l.key + '" class="self-start text-xs font-semibold text-muted underline underline-offset-2 transition-colors duration-150 hover:text-accent">Remove</button>' +
            '</li>';
          }).join('') +
        '</ul>' +
        '<footer class="border-t-2 border-ink px-5 py-4">' +
          '<div class="flex items-baseline justify-between">' +
            '<span class="text-sm font-semibold text-heading">Subtotal</span>' +
            '<span class="font-display text-[22px] font-extrabold text-heading">' + money(subtotal) + '</span>' +
          '</div>' +
          '<p class="mt-1 text-xs text-muted">Taxes and shipping calculated at checkout.</p>' +
          '<a href="/checkout" data-cart-checkout class="fruity-hard mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-ink bg-accent px-6 py-3.5 text-[15px] font-bold text-white transition-transform duration-150 ease-out hover:-translate-y-0.5 active:translate-y-0">Checkout · ' + money(subtotal) + '</a>' +
          '<button type="button" data-cart-empty class="mt-2 w-full text-center text-xs font-semibold text-muted underline underline-offset-2 transition-colors duration-150 hover:text-accent">Empty bag</button>' +
        '</footer>';

      $$('[data-line-minus]', body).forEach(function (b) {
        on(b, 'click', function () { var l = find(b.getAttribute('data-line-minus')); cart.setQty(l.key, l.quantity - 1).then(render); });
      });
      $$('[data-line-plus]', body).forEach(function (b) {
        on(b, 'click', function () { var l = find(b.getAttribute('data-line-plus')); cart.setQty(l.key, l.quantity + 1).then(render); });
      });
      $$('[data-line-remove]', body).forEach(function (b) {
        on(b, 'click', function () { cart.remove(b.getAttribute('data-line-remove')).then(render); });
      });
      on($('[data-cart-empty]', body), 'click', function () { cart.clear().then(render); });

      function find(key) { return cart.lines.filter(function (l) { return l.key === key; })[0] || { quantity: 0 }; }
    }

    function render() { renderCount(); renderBody(); }

    function setOpen(open) {
      shell.setAttribute('aria-hidden', open ? 'false' : 'true');
      swapClasses(shell, open ? 'invisible opacity-0' : 'visible opacity-100', open ? 'visible opacity-100' : 'invisible opacity-0');
      swapClasses(panel, open ? 'translate-x-full' : 'translate-x-0', open ? 'translate-x-0' : 'translate-x-full');
      $$('button, a[href]', shell).forEach(function (b) { b.tabIndex = open ? 0 : -1; });
      lockScroll(open);
      // `fruityCartOpen` is the single source of truth every scroll-driven
      // sticky widget (checkout bar, sticky CTA pill) checks before showing
      // itself. Set it BEFORE touching the bar below so nothing can race it.
      fruityCartOpen = open;
      // Hide sticky checkout bar when drawer is open, restore when closed.
      // The drawer sits at a higher z-index than the sticky bar either way
      // (see snippets/fruity-cart-drawer.liquid), but leaving the bar's own
      // hit area live underneath it would still let a touch reach through to
      // whatever is at that point once the drawer's close animation starts.
      //
      // This now mirrors initStickyCheckout's own apply() exactly (aria-hidden
      // + tabIndex + the transform/pointer-events classes) instead of only
      // touching the transform — leaving aria-hidden="false" behind (as the
      // old code did) meant the bar was still "shown" as far as any later
      // consumer of that attribute was concerned, even though it was
      // offscreen. A later resize/scroll-driven apply() call (mobile browser
      // chrome collapsing while the drawer is open is a real, frequent
      // trigger) would then just re-declare "still past the buy box, show
      // it" with no idea a drawer was covering the page, pulling the bar
      // right back onscreen on top of the drawer's own checkout button.
      var stickyBar = $('[data-fruity-sticky-checkout]');
      if (stickyBar) {
        if (open) {
          stickyBar.setAttribute('aria-hidden', 'true');
          stickyBar.classList.remove('is-visible');
          swapClasses(stickyBar, 'pointer-events-auto translate-y-0', 'pointer-events-none translate-y-[95px] lg:translate-y-[111.1px]');
          var btn = $('button', stickyBar);
          if (btn) btn.tabIndex = -1;
        } else if (window.fruityStickyCheckout) {
          // Re-run the same viewport check initStickyCheckout uses on scroll —
          // closing the drawer doesn't itself fire a scroll event, so without
          // this the bar stayed hidden until the visitor scrolled again.
          window.fruityStickyCheckout.refresh();
        }
      }
      // The sticky CTA pill (a separate widget, [data-fruity-sticky-cta] —
      // see initStickyCta) is also scroll-driven and was never told about the
      // drawer at all before this fix.
      if (window.fruityStickyCta) {
        if (open) window.fruityStickyCta.hide();
        else window.fruityStickyCta.refresh();
      }
    }

    $$('[data-fruity-cart-close]', shell).forEach(function (b) { on(b, 'click', function () { setOpen(false); }); });
    on(shell, 'click', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-fruity-cart-close')) setOpen(false);
    });
    on(cartBtn, 'click', function () { render(); setOpen(true); cart.load().then(render).catch(function () {}); });
    on(window, 'keydown', function (e) {
      if (e.key === 'Escape' && shell.getAttribute('aria-hidden') === 'false') setOpen(false);
    });

    render();
    // Exposed so initBuyBox (the buy box) can refresh + open the drawer after
    // a real POST /cart/add.js succeeds, without reaching into this closure.
    window.fruityCart = { open: function () { render(); setOpen(true); } };
  }

  /* =========================================================== sticky CTA */
  function initStickyCta() {
    var pill = $('[data-fruity-sticky-cta]');
    if (!pill) return;
    function apply() {
      // Never show over an open cart drawer — a resize (mobile browser chrome
      // collapsing/expanding) fires this same handler while the drawer is
      // open and has no other way to know about it. See setOpen()'s comment
      // in initCart for the full story.
      if (fruityCartOpen) {
        pill.setAttribute('aria-hidden', 'true');
        pill.tabIndex = -1;
        swapClasses(pill, 'pointer-events-auto translate-y-0 opacity-100', 'pointer-events-none -translate-y-[57px] opacity-0 lg:-translate-y-[65px]');
        return;
      }
      var trigger = $('[data-sticky-cta-trigger]') || $('.peachy-hero');
      var buyBox = $('#peachy-buy-box');
      var viewH = window.innerHeight;
      var pastHero = trigger ? trigger.getBoundingClientRect().bottom < 0 : (window.scrollY || 0) > viewH;
      var visible = false, suppressed = false;
      if (buyBox) {
        var r = buyBox.getBoundingClientRect();
        visible = r.top < viewH && r.bottom > 0;
        suppressed = r.bottom <= 0;
      }
      var show = pastHero && !visible && !suppressed;
      pill.setAttribute('aria-hidden', show ? 'false' : 'true');
      pill.tabIndex = show ? 0 : -1;
      if (show) swapClasses(pill, 'pointer-events-none -translate-y-[57px] opacity-0 lg:-translate-y-[65px]', 'pointer-events-auto translate-y-0 opacity-100');
      else swapClasses(pill, 'pointer-events-auto translate-y-0 opacity-100', 'pointer-events-none -translate-y-[57px] opacity-0 lg:-translate-y-[65px]');
    }
    onScrollFrame(apply);
    window.fruityStickyCta = {
      refresh: apply,
      hide: function () {
        pill.setAttribute('aria-hidden', 'true');
        pill.tabIndex = -1;
        swapClasses(pill, 'pointer-events-auto translate-y-0 opacity-100', 'pointer-events-none -translate-y-[57px] opacity-0 lg:-translate-y-[65px]');
      },
    };
  }

  /* ============================================================ press bar */
  function initPress() {
    var tabs = $$('[data-fruity-press-tab]');
    var panel = $('#press-quote-panel');
    if (!tabs.length || !panel) return;

    function select(index, focus) {
      tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('aria-expanded', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) swapClasses(tab, 'opacity-45 hover:opacity-70', 'opacity-100');
        else swapClasses(tab, 'opacity-100', 'opacity-45 hover:opacity-70');
        var rule = tab.lastElementChild;
        swapClasses(rule, active ? 'opacity-0' : 'opacity-100', active ? 'opacity-100' : 'opacity-0');
      });
      panel.textContent = tabs[index].getAttribute('data-quote');
      panel.setAttribute('aria-labelledby', tabs[index].id);
      // React remounts the blockquote (key={active.id}), which restarts the
      // fade. Re-triggering the animation by hand is the same effect.
      panel.classList.remove('fruity-quote-fade');
      void panel.offsetWidth;
      panel.classList.add('fruity-quote-fade');
      if (focus) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      on(tab, 'click', function () { select(i); });
      on(tab, 'keydown', function (e) {
        var n = tabs.length;
        if (e.key === 'ArrowRight') { e.preventDefault(); select((i + 1) % n, true); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); select((i - 1 + n) % n, true); }
        else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
        else if (e.key === 'End') { e.preventDefault(); select(n - 1, true); }
      });
    });
  }

  /* ============================================ what's happening timeline */
  var RING_MS = 900, CHECK_MS = 500, LINE_MS = 700, STEP_STAGGER_MS = 220;

  function initTimeline() {
    var list = $('.peachy-happening__timeline');
    if (!list) return;
    var steps = $$('.peachy-happening__step', list);

    // Disarmed: dash the stroke out by its own measured length, so the ring and
    // the tick are invisible until the section scrolls into view.
    steps.forEach(function (step) {
      $$('circle, path', step).forEach(function (geo) {
        if (typeof geo.getTotalLength !== 'function') return;
        var len = geo.getTotalLength();
        geo.style.strokeDasharray = String(len);
        geo.style.strokeDashoffset = String(len);
        geo.style.animation = 'none';
      });
    });

    function arm() {
      steps.forEach(function (step, i) {
        var delay = i * STEP_STAGGER_MS;
        var ring = $('circle', step);
        var check = $('path', step);
        if (ring) { ring.style.strokeDashoffset = '0'; ring.style.animation = 'fruity-ring ' + RING_MS + 'ms ease-out ' + delay + 'ms forwards'; }
        if (check) { check.style.strokeDashoffset = '0'; check.style.animation = 'fruity-check ' + CHECK_MS + 'ms ease-out ' + (delay + RING_MS * 0.6) + 'ms forwards'; }
        var fill = step.querySelector('.bg-accent\\/40');
        if (fill) { fill.style.height = ''; fill.style.animation = 'fruity-line ' + LINE_MS + 'ms ease-out ' + delay + 'ms forwards'; }
        var marker = ring && ring.closest('span');
        if (marker) setTimeout(function () { marker.style.animation = 'fruity-dot-bounce .4s ease-out 1'; }, delay + RING_MS * 0.6 + CHECK_MS);
      });
    }

    if (typeof IntersectionObserver === 'undefined') { arm(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { if (entry.isIntersecting) { arm(); io.disconnect(); } });
    }, { threshold: 0.3 });
    io.observe(list);
  }

  /* ================================================== testimonial slider */
  function initTestimonials() {
    var section = $('.peachy-testimonial-slider');
    if (!section) return;
    var payload = $('[data-fruity-testimonials]', section);
    var track = $('.peachy-testimonial-slider__track', section);
    var tabs = $$('[data-fruity-tst-tab]', section);
    var prev = $('.peachy-testimonial-slider__arrow--prev', section);
    var next = $('.peachy-testimonial-slider__arrow--next', section);
    var fill = $('.peachy-testimonial-slider__progress-fill', section);
    if (!payload || !track) return;

    var all;
    try { all = JSON.parse(payload.textContent); } catch (e) { return; }

    var activeCat = tabs.length ? tabs[0].getAttribute('data-category') : null;
    var rawIndex = 0;
    var visibleCount = window.innerWidth < 768 ? 1 : 3;

    var CARD = 'peachy-testimonial-slider__card flex h-full flex-col gap-3.5 rounded-[20px] border border-heading/10 bg-white p-6 shadow-[0_4px_20px_rgba(42,27,46,0.08)] sm:h-[392.09px] transition-[transform,box-shadow] duration-150 ease-out hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(42,27,46,0.14)] motion-reduce:transition-none motion-reduce:hover:translate-y-0';
    var STAR = '<svg viewBox="0 0 20 20" class="shrink-0 text-accent" style="height:16px;width:16px" fill="currentColor" aria-hidden="true"><path d="M10 1.5l2.59 5.25 5.79.84-4.19 4.08.99 5.77L10 14.77l-5.18 2.67.99-5.77L1.62 7.6l5.79-.84L10 1.5z"></path></svg>';

    function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }

    function cardHtml(q) {
      return '<article class="' + CARD + '">' +
        '<div class="peachy-testimonial-slider__person flex items-center gap-3">' +
          '<img alt="' + esc(q.avatar_alt) + '" loading="lazy" width="160" height="160" decoding="async" class="h-12 w-12 shrink-0 rounded-full object-cover" style="color:transparent" src="' + assetUrl(q.avatar) + '">' +
          '<div><p class="font-display text-[15px] leading-[22.5px] font-semibold text-heading">' + esc(q.name) + '</p>' +
          '<p class="flex items-center gap-1 text-[13px] leading-[18px] text-body/70">' +
          '<svg viewBox="0 0 16 16" class="h-3 w-3 text-sky-500" fill="currentColor" aria-hidden="true"><path d="M8 0l1.8 1.2 2.1-.4.9 2 2 .9-.4 2.1L16 8l-1.2 1.8.4 2.1-2 .9-.9 2-2.1-.4L8 16l-1.8-1.2-2.1.4-.9-2-2-.9.4-2.1L0 8l1.2-1.8-.4-2.1 2-.9.9-2 2.1.4L8 0z"></path></svg>Verified Customer</p></div>' +
        '</div>' +
        '<div class="peachy-stars flex items-center gap-1.5">' + STAR + STAR + STAR + STAR + STAR + '</div>' +
        '<h3 class="peachy-testimonial-slider__title font-display text-[17px] leading-[28.05px] font-bold text-heading">' + esc(q.title) + '</h3>' +
        '<blockquote class="peachy-testimonial-slider__text flex-1 overflow-hidden text-[15px] leading-[22.5px] font-medium text-body">“' + esc(q.quote) + '”</blockquote>' +
      '</article>';
    }

    function render() {
      var items = all.filter(function (q) { return q.category_id === activeCat; });
      var maxIndex = Math.max(0, items.length - visibleCount);
      var pageCount = maxIndex + 1;
      var index = Math.min(rawIndex, maxIndex);
      var slideW = 100 / visibleCount;

      track.innerHTML = items.map(function (q, i) {
        return '<div class="shrink-0 px-2.5" style="width:' + slideW + '%" aria-hidden="' + (i === index ? 'false' : 'true') + '">' + cardHtml(q) + '</div>';
      }).join('');
      track.style.transform = 'translateX(-' + index * slideW + '%)';
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === maxIndex;
      if (fill) {
        var w = 100 / pageCount;
        fill.style.width = w + '%';
        fill.style.marginLeft = index * w + '%';
      }
      rawIndex = index;
      return { maxIndex: maxIndex };
    }

    tabs.forEach(function (tab) {
      on(tab, 'click', function () {
        activeCat = tab.getAttribute('data-category');
        rawIndex = 0;
        tabs.forEach(function (t) {
          var active = t === tab;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
          if (active) swapClasses(t, 'border-heading/12 bg-white text-heading hover:border-heading/30 hover:-translate-y-0.5', 'border-accent bg-accent text-white');
          else swapClasses(t, 'border-accent bg-accent text-white', 'border-heading/12 bg-white text-heading hover:border-heading/30 hover:-translate-y-0.5');
        });
        render();
      });
    });
    on(prev, 'click', function () { rawIndex = Math.max(0, rawIndex - 1); render(); });
    on(next, 'click', function () { rawIndex = render().maxIndex === rawIndex ? rawIndex : rawIndex + 1; render(); });
    on(window, 'resize', function () {
      var vc = window.innerWidth < 768 ? 1 : 3;
      if (vc !== visibleCount) { visibleCount = vc; render(); }
    });
    render();
  }

  /* The theme's images are flat files in /assets; a name is enough to find one. */
  var ASSET_BASE = null;
  function assetUrl(name) {
    if (!name) return '';
    if (name.indexOf('/') === 0 || name.indexOf('http') === 0) return name;
    if (ASSET_BASE === null) {
      var probe = document.querySelector('img[src*="/assets/"], link[href*="/assets/"]');
      var src = probe ? (probe.src || probe.href) : '/assets/x';
      ASSET_BASE = src.slice(0, src.lastIndexOf('/') + 1);
    }
    return ASSET_BASE + name;
  }

  /* ============================================================== reviews */
  function initReviews() {
    var section = $('#peachy-reviews');
    if (!section) return;
    var list = $('ul.mt-8.flex.flex-col.gap-6', section) || section.querySelector('.peachy-reviews__judgeme ul');
    var payload = $('[data-fruity-review-dataset]', section);
    var writeBtn = $('[data-fruity-review-write]', section);
    var composerTpl = $('[data-fruity-composer-template]', section);
    var sortSel = $('[data-fruity-review-sort]', section);
    var chips = $$('[data-fruity-review-chip]', section);
    var bars = $$('[data-fruity-review-bar]', section);
    var pageBtns = $$('[data-fruity-review-page]', section);
    var pagePrev = $('[data-fruity-review-prev]', section);
    var pageNext = $('[data-fruity-review-next]', section);

    var filter = 0, sortKey = 'newest', page = 1;
    var pageSize = $$('li', list).length || 5;

    var all = [];
    if (payload) { try { all = JSON.parse(payload.textContent); } catch (e) { all = []; } }

    function esc(v) { return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }

    function starRow(rating) {
      var out = '<span class="inline-flex items-center gap-0.5" role="img" aria-label="' + rating + ' out of 5 stars">';
      for (var i = 1; i <= 5; i++) {
        out += '<svg viewBox="0 0 20 20" class="h-4 w-4 shrink-0 ' + (i <= rating ? 'fill-accent' : 'fill-taupe') +
          '" aria-hidden="true"><path d="M10 1.5l2.59 5.25 5.79.84-4.19 4.08.99 5.77L10 14.77l-5.18 2.67.99-5.77L1.62 7.59l5.79-.84L10 1.5z"></path></svg>';
      }
      return out + '</span>';
    }

    /* One builder, same markup as the Liquid card — the two must not drift, so the
     * classes are copied from the section rather than re-derived. */
    function cardHtml(r) {
      var avatar = r.avatar
        ? '<div class="relative h-12 w-12 shrink-0 overflow-hidden rounded-full bg-cream">' +
            '<img alt="' + esc(r.avatar_alt) + '" loading="lazy" decoding="async" class="object-cover" style="position:absolute;height:100%;width:100%;left:0;top:0;right:0;bottom:0;color:transparent" src="' + assetUrl(r.avatar) + '">' +
          '</div>'
        : '<span aria-hidden="true" class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-heading text-base font-bold text-white">' + esc(String(r.author).trim().charAt(0).toUpperCase()) + '</span>';
      var flag = r.country === 'US'
        ? '<img alt="US" loading="lazy" width="16" height="11" decoding="async" class="h-[11px] w-[16px]" style="color:transparent" src="' + assetUrl(r.flag) + '">' : '';
      var verified = r.verified
        ? '<span class="inline-flex items-center gap-1 text-xs font-medium text-accent"><img alt="Verified Checkmark" loading="lazy" width="17" height="16" decoding="async" class="h-[16px] w-[17px]" style="color:transparent" src="' + assetUrl('fruity-verified.svg') + '">Verified buyer</span>' : '';
      var photo = r.photo
        ? '<div class="mt-4 h-24 w-24 overflow-hidden rounded-xl bg-cream"><img alt="' + esc(r.photo_alt) + '" loading="lazy" width="96" height="96" decoding="async" class="h-full w-full object-cover" style="color:transparent" src="' + assetUrl(r.photo) + '"></div>' : '';
      return '<li class="rounded-2xl border border-taupe/40 bg-white p-6 opacity-100 transition-opacity duration-[180ms] ease-out md:p-7">' +
        '<div class="flex flex-wrap items-center gap-4">' + avatar +
          '<div class="flex flex-1 flex-col gap-1">' +
            '<span class="inline-flex flex-wrap items-center gap-1.5 text-sm font-semibold text-heading-2">' + esc(r.author) + flag + '</span>' + verified +
          '</div>' +
          '<span class="text-xs text-muted">' + esc(r.date_label) + '</span>' +
        '</div>' +
        '<div class="mt-5">' + starRow(r.rating) + '</div>' +
        '<p class="mt-5 text-lg leading-[1.65] text-body">' + esc(r.body) + '</p>' + photo +
        '<div class="mt-5 flex items-center justify-between border-t border-taupe/30 pt-4">' +
          '<span class="text-sm text-muted">Was this review helpful?</span>' +
          '<button type="button" data-fruity-review-helpful class="flex items-center gap-1.5 rounded-full border border-taupe px-3 py-1 text-xs font-medium text-body transition-[border-color,transform] duration-150 ease-out hover:-translate-y-0.5 motion-reduce:transition-none" aria-label="Mark review by ' + esc(r.author) + ' as helpful">Helpful</button>' +
        '</div>' +
      '</li>';
    }

    function paginationHtml(pageCount, current) {
      var out = '<button type="button"' + (current === 1 ? ' disabled' : '') + ' aria-label="Previous page" data-fruity-review-prev class="rounded-full border border-taupe px-3 py-1.5 text-sm text-body transition-[border-color] duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-40">Back</button>';
      for (var i = 1; i <= pageCount; i++) {
        out += '<button type="button" aria-label="Page ' + i + '"' + (i === current ? ' aria-current="page"' : '') +
          ' data-fruity-review-page="' + i + '" class="h-8 w-8 rounded-full text-sm font-medium transition-[border-color,transform] duration-150 ease-out ' +
          (i === current ? 'bg-accent text-white' : 'border border-taupe bg-white text-body hover:-translate-y-0.5') + '">' + i + '</button>';
      }
      return out + '<button type="button"' + (current === pageCount ? ' disabled' : '') + ' aria-label="Next page" data-fruity-review-next class="rounded-full border border-taupe px-3 py-1.5 text-sm text-body transition-[border-color] duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-40">Next</button>';
    }

    var nav = $('nav[aria-label="Reviews pagination"]', section);

    /* Filter and sort run over the WHOLE dataset, then paginate — the same shape
     * as the useMemo chain in Reviews.tsx. Filtering only the rendered page would
     * hide the 4-star review that lives on page two. */
    function apply() {
      if (!all.length) return;
      var items = filter === 0 ? all.slice() : all.filter(function (r) { return r.rating === filter; });
      if (sortKey === 'highest') items.sort(function (a, b) { return b.rating - a.rating; });
      else if (sortKey === 'lowest') items.sort(function (a, b) { return a.rating - b.rating; });
      else items.sort(function (a, b) { return new Date(b.date) - new Date(a.date); });

      var pageCount = Math.max(1, Math.ceil(items.length / pageSize));
      page = Math.min(page, pageCount);
      list.innerHTML = items.slice((page - 1) * pageSize, page * pageSize).map(cardHtml).join('');
      if (nav) {
        nav.innerHTML = paginationHtml(pageCount, page);
        $$('[data-fruity-review-page]', nav).forEach(function (b) {
          on(b, 'click', function () { page = parseInt(b.getAttribute('data-fruity-review-page'), 10); apply(); });
        });
        on($('[data-fruity-review-prev]', nav), 'click', function () { page = Math.max(1, page - 1); apply(); });
        on($('[data-fruity-review-next]', nav), 'click', function () { page = Math.min(pageCount, page + 1); apply(); });
      }
      wireHelpful();
    }

    function wireHelpful() {
      $$('[data-fruity-review-helpful]', section).forEach(function (btn) {
        if (btn.dataset.wired) return;
        btn.dataset.wired = '1';
        var n = 0;
        on(btn, 'click', function () { n += 1; btn.textContent = 'Helpful ' + n; });
      });
    }

    chips.forEach(function (chip) {
      on(chip, 'click', function () {
        filter = parseInt(chip.getAttribute('data-star'), 10) || 0;
        chips.forEach(function (c) {
          var active = c === chip;
          c.setAttribute('aria-checked', active ? 'true' : 'false');
          if (active) swapClasses(c, 'border-taupe bg-white text-body hover:-translate-y-0.5', 'border-accent bg-accent text-white');
          else swapClasses(c, 'border-accent bg-accent text-white', 'border-taupe bg-white text-body hover:-translate-y-0.5');
        });
        page = 1;
        apply();
      });
    });
    // The histogram bars are a second way to press the same filter.
    bars.forEach(function (bar) {
      on(bar, 'click', function () {
        var star = bar.getAttribute('data-star');
        var chip = chips.filter(function (c) { return c.getAttribute('data-star') === star; })[0];
        if (chip) chip.click();
      });
    });
    on(sortSel, 'change', function () { sortKey = sortSel.value; page = 1; apply(); });

    if (writeBtn && composerTpl) {
      var composer = null;
      on(writeBtn, 'click', function () {
        if (composer) {
          composer.remove(); composer = null;
          writeBtn.setAttribute('aria-expanded', 'false');
          writeBtn.textContent = 'Write a review';
          return;
        }
        composer = composerTpl.content.firstElementChild.cloneNode(true);
        var head = writeBtn.closest('.flex.flex-col.gap-8') || writeBtn.parentElement;
        head.insertAdjacentElement('afterend', composer);
        writeBtn.setAttribute('aria-expanded', 'true');
        writeBtn.textContent = 'Cancel review';
        wireComposer(composer);
      });
    }

    function wireComposer(form) {
      var score = 0;
      var stars = $$('fieldset button', form);
      stars.forEach(function (btn, i) {
        on(btn, 'click', function () {
          score = i + 1;
          stars.forEach(function (st, j) {
            st.setAttribute('aria-pressed', j === i ? 'true' : 'false');
            swapClasses(st, j <= i ? 'text-heading/25' : 'text-accent', j <= i ? 'text-accent' : 'text-heading/25');
          });
          var err = $('[data-composer-error]', form);
          if (err) err.remove();
        });
      });
      on(form, 'submit', function (e) {
        e.preventDefault();
        /* A star rating is the one field a native `required` cannot cover: the
         * input behind it is hidden, so the browser refuses to focus it and the
         * form would fail silently. */
        if (score === 0) {
          if (!$('[data-composer-error]', form)) {
            var msg = document.createElement('p');
            msg.setAttribute('data-composer-error', '');
            msg.className = 'text-xs font-semibold text-accent';
            msg.textContent = 'Pick a star rating first.';
            form.appendChild(msg);
          }
          return;
        }
        form.innerHTML = '<p class="text-sm font-bold text-heading">Thanks — your review is in the queue.</p>' +
          '<p class="text-xs text-muted">Reviews are published once they are verified against an order.</p>';
      });
    }

    // Video stories removed per ticket 6 - stories are now static image cards


    wireHelpful();
    void pageBtns; void pagePrev; void pageNext;
  }

  /* ============================================================= buy box */
  function initGallery() {
    var gallery = $('.peachy-gallery');
    if (!gallery) return;
    var track = $('.peachy-gallery__track', gallery);
    var rail = $('.peachy-gallery__thumbnails');
    var thumbs = rail ? $$('[data-fruity-thumb]', rail) : [];
    var slides = track ? $$('.peachy-gallery__slide', track) : [];
    var dotsWrap = $('[data-fruity-gallery-dots]', gallery);
    var dots = dotsWrap ? $$('[data-fruity-dot]', dotsWrap) : [];
    if (!track) return;
    var active = 0;

    function paint(index) {
      if (index === active) return;
      active = index;
      slides.forEach(function (s, i) { s.setAttribute('aria-hidden', i === index ? 'false' : 'true'); });
      thumbs.forEach(function (t, i) {
        var on_ = i === index;
        t.setAttribute('aria-current', on_ ? 'true' : 'false');
        if (on_) swapClasses(t, 'shadow-[0_0_0_1px_rgba(38,7,31,0.12)]', 'shadow-[0_0_0_2px_var(--ink)]');
        else swapClasses(t, 'shadow-[0_0_0_2px_var(--ink)]', 'shadow-[0_0_0_1px_rgba(38,7,31,0.12)]');
      });
      // The rail overflows its window, so slide 12's thumb is off-screen until
      // it is scrolled to.
      var thumb = thumbs[index];
      if (rail && thumb) rail.scrollTo({ left: thumb.offsetLeft - rail.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      dots.forEach(function (d, i) {
        var on_ = i === index;
        d.setAttribute('aria-selected', on_ ? 'true' : 'false');
        d.classList.toggle('is-active', on_);
      });
    }

    /* Active slide is READ from scrollLeft, never stored — a swipe fires no
     * click, and a stored index would desync the thumbnails from the frame. */
    var frame = 0;
    on(track, 'scroll', function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        var w = track.clientWidth || 1;
        paint(Math.min(Math.max(Math.round(track.scrollLeft / w), 0), slides.length - 1));
      });
    }, { passive: true });

    thumbs.forEach(function (t, i) {
      on(t, 'click', function () { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); });
    });

    // 1.png — mobile-only: dot indicators + auto-advance every ~4s. Native
    // scroll-snap already makes the track swipeable (same `.peachy-gallery__
    // track` the thumbnails drive); this only adds the dots' own click-to-go
    // and a timer that pauses while the visitor is touching the gallery and
    // stays paused once they've swiped manually — an autoplay that fights a
    // swipe mid-gesture is worse than none.
    if (dots.length) {
      dots.forEach(function (d, i) {
        on(d, 'click', function () {
          userInteracted = true;
          stopAutoplay();
          track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        });
      });
    }
    var AUTOPLAY_MS = 4000;
    var autoplayTimer = null;
    var userInteracted = false;
    function isMobile() { return window.matchMedia('(max-width: 767.98px)').matches; }
    function startAutoplay() {
      if (autoplayTimer || userInteracted || !isMobile() || slides.length < 2) return;
      autoplayTimer = setInterval(function () {
        var next = (active + 1) % slides.length;
        track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
      }, AUTOPLAY_MS);
    }
    function stopAutoplay() {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; }
    }
    on(track, 'pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.pointerType === 'pen') { userInteracted = true; stopAutoplay(); }
    });
    on(track, 'wheel', function () { userInteracted = true; stopAutoplay(); }, { passive: true });
    startAutoplay();
  }

  function initModals() {
    // Supplement Facts, restored: cloned from a <template> (no boxes at rest,
    // same pattern as the HSA/FSA sheet below), not the old always-in-DOM
    // invisible/opacity-0 toggle. Focus moves to the dialog's close button on
    // open and back to the trigger link on close, same as the HSA sheet plus
    // an explicit focus-restore so keyboard/AT users don't lose their place.
    var factsOpen = $('[data-fruity-facts-open]');
    var factsTpl = $('[data-fruity-facts-template]');
    if (factsOpen && factsTpl) {
      var factsSheet = null;
      var closeFacts = function () {
        if (!factsSheet) return;
        factsSheet.remove(); factsSheet = null;
        factsOpen.setAttribute('aria-expanded', 'false');
        lockScroll(false);
        factsOpen.focus();
      };
      on(factsOpen, 'click', function () {
        if (factsSheet) return closeFacts();
        factsSheet = factsTpl.content.firstElementChild.cloneNode(true);
        document.body.appendChild(factsSheet);
        factsOpen.setAttribute('aria-expanded', 'true');
        lockScroll(true);
        $$('[data-fruity-facts-close]', factsSheet).forEach(function (b) { on(b, 'click', closeFacts); });
        var closeBtn = $('.pfacts__closebtn', factsSheet);
        if (closeBtn) closeBtn.focus();
      });
      on(window, 'keydown', function (e) { if (e.key === 'Escape' && factsSheet) closeFacts(); });
    }

    var hsaOpen = $('[data-fruity-hsa-open]');
    var hsaTpl = $('[data-fruity-hsa-template]');
    if (hsaOpen && hsaTpl) {
      var sheet = null;
      var close = function () {
        if (!sheet) return;
        sheet.remove(); sheet = null;
        hsaOpen.setAttribute('aria-expanded', 'false');
        lockScroll(false);
      };
      on(hsaOpen, 'click', function () {
        if (sheet) return close();
        sheet = hsaTpl.content.firstElementChild.cloneNode(true);
        document.body.appendChild(sheet);
        hsaOpen.setAttribute('aria-expanded', 'true');
        lockScroll(true);
        var backdrop = $('.phsab__backdrop', sheet);
        on(backdrop, 'click', close);
        var closeBtn = $('.phsab__closebtn', sheet);
        on(closeBtn, 'click', close);
        if (closeBtn) closeBtn.focus();
      });
      on(window, 'keydown', function (e) { if (e.key === 'Escape') close(); });
    }
  }

  function initBuyBox() {
    var section = $('#peachy-buy-box');
    if (!section) return;
    var tierInputs = $$('[data-fruity-tier]', section);
    if (!tierInputs.length) return;

    var tiers = tierInputs.map(function (input) {
      return {
        input: input,
        row: input.closest('[data-fruity-tier-row]'),
        id: input.value,
        qty: parseInt(input.getAttribute('data-qty'), 10),
        perCents: parseInt(input.getAttribute('data-per-cents'), 10),
        totalCents: parseInt(input.getAttribute('data-total-cents'), 10),
        originalCents: parseInt(input.getAttribute('data-original-cents'), 10),
        variantKey: input.getAttribute('data-variant-key') || '',
      };
    });
    var selected = 0;
    var status = 'idle'; // idle | adding | added | error
    var gifts = $$('[data-fruity-gift]', section).map(function (el) {
      return { el: el, unlock: parseInt(el.getAttribute('data-unlock-qty'), 10) || 0, copy: $('[data-fruity-gift-copy]', el) };
    });

    // `data-fruity-variant-map` only exists in the rendered HTML when the
    // section has a real Shopify product wired up (the `target_product`
    // fallback in sections/fruity-buybox.liquid — see dev/a_buybox.py). No
    // map, no real variants: the tier card still works as a configurator
    // (for the pixel-locked demo pages), but the CTA has nothing real to add
    // and is a no-op, same as it always was without a connected product.
    // Keys are handleized variant titles — either the flavour ("black-cherry")
    // on a single-variant product, or a bag-count title ("3-bags") if the
    // store followed HANDOVER.md's option 1.
    var variantMapEl = $('[data-fruity-variant-map]', section);
    var variantMap = null;
    var connected = false;
    if (variantMapEl) {
      try {
        variantMap = JSON.parse(variantMapEl.textContent).flavours || {};
        connected = true;
      } catch (e) { variantMap = null; connected = false; }
    }
    var variantInput = $('[data-fruity-variant-input]', section);
    var idleLabel = ($('[data-fruity-cta-label]', section) || {}).textContent || 'Add to Cart';

    /* Subscribe / one-time — only present in the DOM when the connected
     * product actually has selling plans (see the `fruity_plan_group != blank`
     * guard in dev/a_buybox.py), so every selector below is legitimately
     * empty on a connected product with no plans and on every unconnected
     * page. Nothing here assumes the control exists — this is the sticky
     * bar's territory to leave alone, not touched beyond feeding it a price. */
    var sellingPlanInput = $('[data-fruity-selling-plan-input]', section);
    var purchaseRadios = $$('[data-fruity-purchase-radio]', section);
    var cadenceGroup = $('[data-fruity-cadence-group]', section);
    var cadenceRadios = $$('[data-fruity-cadence]', section);
    var purchaseType = 'onetime'; // the store has no "default plan" flag to read — see comment on cadenceRadios below
    var sellingPlanId = cadenceRadios.length ? cadenceRadios[0].value : null;

    /* Which real variant + quantity a tier actually adds. If the tier has a
     * `variant_title` theme setting that resolves in the variant map (see
     * HANDOVER.md option 1: dedicated "1 Bag"/"2 Bags"/"3 Bags" variants),
     * that variant is added at quantity 1 and its REAL price/compare-at wins.
     * Otherwise the connected product's own default variant is added at
     * quantity = tier.qty, and price stays the curated theme-settings shape
     * (display-only — see the pre-launch blocker in HANDOVER.md). */
    function resolveVariant(tier) {
      if (!variantMap) return { v: null, qty: tier.qty, usesVariant: false };
      if (tier.variantKey && variantMap[tier.variantKey]) {
        return { v: variantMap[tier.variantKey], qty: 1, usesVariant: true };
      }
      var refId = variantInput ? variantInput.value : null;
      var refKey = null;
      for (var k in variantMap) { if (String(variantMap[k].id) === String(refId)) { refKey = k; break; } }
      return { v: refKey ? variantMap[refKey] : null, qty: tier.qty, usesVariant: false };
    }

    function connectedTotals(tier) {
      var resolved = resolveVariant(tier);
      if (resolved.usesVariant && resolved.v) {
        var price = resolved.v.price / 100;
        var compareAt = resolved.v.compareAtPrice > 0 ? resolved.v.compareAtPrice / 100 : price;
        return { total: price, original: compareAt, resolved: resolved };
      }
      // No dedicated variant for this tier: the tier's percent-off is a
      // marketing shape applied to the theme's base_price setting, not live
      // Shopify data — see the pre-launch blocker in HANDOVER.md. Nothing
      // here invents a discount Shopify will actually charge.
      return { total: tier.totalCents / 100, original: tier.originalCents / 100, resolved: resolved };
    }

    /* Subscribing prices the resolved variant off ITS OWN
     * `selling_plan_allocations` entry for the chosen plan — the real,
     * pre-computed Shopify number (see dev/a_buybox.py's variant-map
     * comment). Falls back to the one-time totals if the resolved variant
     * has no allocation for this plan, rather than silently pricing wrong. */
    function subscribeTotals(tier) {
      var resolved = resolveVariant(tier);
      var v = resolved.v;
      var plan = v && v.sellingPlans && sellingPlanId ? v.sellingPlans[sellingPlanId] : null;
      if (!v || !plan) return connectedTotals(tier);
      var base = v.price / 100;
      return { total: (plan.price / 100) * resolved.qty, original: base * resolved.qty, resolved: resolved };
    }

    function selectTier(index) {
      selected = index;
      render();
    }

    function render() {
      var tier = tiers[selected];
      var total, original;
      var subscribing = purchaseType === 'subscribe' && sellingPlanId;
      if (subscribing) {
        var st = subscribeTotals(tier);
        total = st.total; original = st.original;
      } else if (connected) {
        var ct = connectedTotals(tier);
        total = ct.total; original = ct.original;
      } else {
        total = tier.totalCents / 100;
        original = tier.originalCents / 100;
      }
      var perDay = total / (tier.qty * 30);

      // Purchase-type + cadence radios: same swapClasses pattern as the tier
      // rows below, plus the plan id going into the no-JS/AJAX hidden input.
      if (sellingPlanInput) sellingPlanInput.value = subscribing ? sellingPlanId : '';
      purchaseRadios.forEach(function (input) {
        var option = input.closest('[data-fruity-purchase-option]');
        var dot = $('[data-fruity-purchase-dot]', option);
        var checked = input.value === purchaseType;
        input.checked = checked;
        if (checked) swapClasses(option, 'border-heading/15', 'border-ink shadow-[3px_3px_0_0_var(--ink)]');
        else swapClasses(option, 'border-ink shadow-[3px_3px_0_0_var(--ink)]', 'border-heading/15');
        if (checked) swapClasses(dot, 'border-heading/20', 'border-ink bg-accent');
        else swapClasses(dot, 'border-ink bg-accent', 'border-heading/20');
      });
      if (cadenceGroup) {
        if (purchaseType === 'subscribe') swapClasses(cadenceGroup, 'hidden', 'flex flex-wrap');
        else swapClasses(cadenceGroup, 'flex flex-wrap', 'hidden');
      }
      cadenceRadios.forEach(function (input) {
        var option = input.closest('[data-fruity-cadence-option]');
        var checked = input.value === sellingPlanId;
        input.checked = checked;
        if (checked) swapClasses(option, 'border-heading/20 bg-white text-heading hover:border-heading/40', 'border-ink bg-accent text-white shadow-[2px_2px_0_0_var(--ink)]');
        else swapClasses(option, 'border-ink bg-accent text-white shadow-[2px_2px_0_0_var(--ink)]', 'border-heading/20 bg-white text-heading hover:border-heading/40');
      });

      tiers.forEach(function (t, i) {
        t.input.checked = i === selected;
        if (t.row) t.row.classList.toggle('is-selected', i === selected);
      });

      gifts.forEach(function (g) {
        var locked = tier.qty < g.unlock;
        g.el.classList.toggle('is-locked', locked);
        if (g.copy) g.copy.textContent = locked ? 'Unlocks at ' + g.unlock + ' bag' + (g.unlock === 1 ? '' : 's') : '';
      });

      // Tightened copy (13.png/14.png bug): "Add to Cart · 3 Bags" wraps to a
      // second line at 393px/360px and crushes the price pill next to it
      // (worst case with a strikethrough compare-at price showing too — see
      // the price pill block below). "Add 3 Bags" is short enough to never
      // wrap at either width; the single-bag case keeps the familiar label
      // since there's no count worth calling out.
      var qtyIdleLabel = tier.qty === 1 ? idleLabel : 'Add ' + tier.qty + ' Bags';
      $$('[data-fruity-cta-label]', section).forEach(function (el) {
        el.textContent = status === 'adding' ? 'Adding…' : status === 'added' ? 'Added' : status === 'error' ? 'Try again' : qtyIdleLabel;
      });
      // Error state reuses the theme's existing red-100/red-700 utilities
      // (already present in the purged Tailwind bundle for form-error text
      // elsewhere) — there is no bespoke "danger button" class to reach for.
      $$('[data-fruity-add]', section).forEach(function (el) {
        if (status === 'error') swapClasses(el, 'bg-accent text-white', 'bg-red-100 text-red-700');
        else swapClasses(el, 'bg-red-100 text-red-700', 'bg-accent text-white');
      });
      // These elements also exist on the sticky bar (see dev/a_buybox.py —
      // both checkout buttons share the same data attributes), so this is
      // the one place that feeds it a price: nothing about the sticky bar's
      // own show/hide/suppression logic in initStickyCheckout() below is
      // touched here.
      $$('[data-fruity-price-total]', section).forEach(function (el) {
        el.textContent = money(total);
        var pill = el.parentElement;
        var strike = $('[data-fruity-price-original]', pill);
        if (original > total) {
          if (!strike) {
            strike = document.createElement('span');
            strike.setAttribute('data-fruity-price-original', '');
            // `fruity-price-original` is a stable hook for both buttons'
            // price pills (see the mobile no-wrap CSS in the section's own
            // {% stylesheet %} block) — the BEM class only existed on the
            // sticky bar's pill, not the main button's.
            strike.className = pill.classList.contains('peachy-bundle-checkout__price')
              ? 'peachy-bundle-checkout__price-original fruity-price-original text-[14px] font-semibold leading-[14px] text-white/75 line-through'
              : 'fruity-price-original text-sm font-semibold text-white/70 line-through';
            pill.insertBefore(strike, el);
          }
          strike.textContent = money(original);
        } else if (strike) {
          strike.remove();
        }
      });
      $$('[data-fruity-price-perday]', section).forEach(function (el) {
        el.textContent = (el.className.indexOf('peachy-bundle-checkout__price-perday') >= 0 ? '· ' : '') + money(perDay) + '/day';
      });
    }

    tierInputs.forEach(function (input, i) { on(input, 'change', function () { selectTier(i); }); });

    purchaseRadios.forEach(function (input) {
      on(input, 'change', function () { purchaseType = input.value; render(); });
    });
    cadenceRadios.forEach(function (input) {
      on(input, 'change', function () { sellingPlanId = input.value; render(); });
    });

    $$('[data-fruity-add]', section).forEach(function (btn) {
      on(btn, 'click', function (evt) {
        // Real product wired up: this button also carries `form="fruity-buybox-form"`
        // (see dev/a_buybox.py) so it's a genuine submit button — the no-JS
        // fallback, which always submits the base variant at quantity 1.
        // With JS running we always intercept and go through /cart/add.js
        // instead, so the real tier quantity/variant gets added.
        if (connected && evt && evt.preventDefault) evt.preventDefault();
        if (status === 'adding') return;
        if (!connected) return; // nothing real to add — no product connected to this section

        var tier = tiers[selected];
        var subscribingNow = purchaseType === 'subscribe' && sellingPlanId;
        var resolved = resolveVariant(tier);
        if (!resolved.v || !resolved.v.id) { status = 'error'; render(); return; }

        var item = { id: resolved.v.id, quantity: resolved.qty };
        if (subscribingNow && resolved.v.sellingPlans && resolved.v.sellingPlans[sellingPlanId]) {
          item.selling_plan = parseInt(sellingPlanId, 10);
        }

        status = 'adding'; render();
        // Real choreography: "adding" holds for the request, but never for
        // less than 450ms so a fast response doesn't just flash past it.
        var settle = new Promise(function (resolve) { setTimeout(resolve, 450); });
        Promise.all([cart.add([item]), settle]).then(function () {
          status = 'added'; render();
          if (window.fruityCart) window.fruityCart.open();
          setTimeout(function () { if (status === 'added') { status = 'idle'; render(); } }, 1750);
        }).catch(function () {
          status = 'error'; render();
          setTimeout(function () { if (status === 'error') { status = 'idle'; render(); } }, 3000);
        });
      });
    });

    render();
  }

  function initStickyCheckout() {
    var bar = $('[data-fruity-sticky-checkout]');
    if (!bar) return;
    var btn = $('button', bar);
    function apply() {
      // Never show over an open cart drawer — see the matching comment on
      // initStickyCta's apply() and on setOpen() in initCart. Without this
      // guard, a `resize` event (mobile browser chrome collapsing while the
      // drawer sits open is a common real trigger) re-runs this same
      // "am I past the buy box" check with zero awareness the drawer exists,
      // and happily pulls the bar back onscreen on top of it.
      if (fruityCartOpen) {
        bar.setAttribute('aria-hidden', 'true');
        bar.classList.remove('is-visible');
        if (btn) btn.tabIndex = -1;
        swapClasses(bar, 'pointer-events-auto translate-y-0', 'pointer-events-none translate-y-[95px] lg:translate-y-[111.1px]');
        return;
      }
      var buyBox = $('#peachy-buy-box');
      // "The buy box has fully left the top of the viewport" — bisected against
      // the live target at bottom=5px off / bottom=-1px on. Not a scroll depth.
      var show = buyBox ? buyBox.getBoundingClientRect().bottom <= 0 : false;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (btn) btn.tabIndex = show ? 0 : -1;
      if (show) { bar.classList.add('is-visible'); swapClasses(bar, 'pointer-events-none translate-y-[95px] lg:translate-y-[111.1px]', 'pointer-events-auto translate-y-0'); }
      else { bar.classList.remove('is-visible'); swapClasses(bar, 'pointer-events-auto translate-y-0', 'pointer-events-none translate-y-[95px] lg:translate-y-[111.1px]'); }
    }
    onScrollFrame(apply);
    // The cart drawer force-hides this bar while open (see initCart's setOpen)
    // without a real scroll happening, so nothing re-runs `apply` on close —
    // the bar stayed hidden until the visitor's next scroll. Exposed so
    // initCart can re-run the SAME viewport check right when it closes.
    window.fruityStickyCheckout = { refresh: apply };
  }

  /* =============================================================== footer */
  function initFooter() {
    $$('[data-fruity-footer-toggle]').forEach(function (btn) {
      var listId = btn.getAttribute('aria-controls');
      var list = document.getElementById(listId);
      var plus = btn.lastElementChild;
      if (!list) return;
      on(btn, 'click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        list.classList.toggle('hidden', open);
        list.classList.toggle('flex', !open);
        if (plus) plus.classList.toggle('rotate-45', !open);
      });
    });

    // The footer newsletter is a real Shopify customer form
    // (sections/fruity-footer.liquid, {% form 'customer' %}); no JS needed.
  }

  /* ============================================================ cart page */
  // sections/fruity-cart.liquid is server-rendered from the real `cart`
  // object, so the initial paint is always correct with JS off. This just
  // wires the qty steppers/remove buttons to the real /cart/change.js
  // endpoint (same `cart` object as the drawer) and reloads so every derived
  // bit — line totals, subtotal, free-shipping bar, the empty state — stays
  // in lockstep with the section's own Liquid rather than duplicating that
  // math a second time in JS.
  function initCartPage() {
    var root = $('[data-fruity-cart-page]');
    if (!root) return;

    function busy(row, isBusy) {
      $$('button', row).forEach(function (b) { b.disabled = isBusy; });
    }

    $$('[data-fruity-cart-page-line]', root).forEach(function (row) {
      var key = row.getAttribute('data-line-key');
      var valueEl = $('[data-fruity-cart-page-qty-value]', row);
      var qty = parseInt(valueEl.textContent, 10) || 0;

      function change(next) {
        busy(row, true);
        cart.setQty(key, next).then(function () { window.location.reload(); })
          .catch(function () { busy(row, false); });
      }

      on($('[data-fruity-cart-page-minus]', row), 'click', function () { change(qty - 1); });
      on($('[data-fruity-cart-page-plus]', row), 'click', function () { change(qty + 1); });
      on($('[data-fruity-cart-page-remove]', row), 'click', function () { change(0); });
    });
  }

  /* ================================================================= boot */
  function boot() {
    initAnnouncement();
    initHeader();
    initNavDrawer();
    initCart();
    initStickyCta();
    initPress();
    initTimeline();
    initTestimonials();
    initReviews();
    initGallery();
    initModals();
    initBuyBox();
    initStickyCheckout();
    initCartPage();
    initFooter();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
